import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, BarChart3, Globe, TrendingUp, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import KineticHero from '@/components/KineticHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMPARE_SECTIONS = [
  { key: 'country_snapshot', label: 'Country Snapshot', icon: Globe },
  { key: 'executive_summary', label: 'Executive Summary', icon: TrendingUp },
  { key: 'market_opportunity', label: 'Market Opportunity', icon: BarChart3 },
  { key: 'risk_analysis', label: 'Risk Analysis', icon: AlertTriangle },
  { key: 'financial_projections', label: 'Financial Projections', icon: TrendingUp },
  { key: 'sovereign_risk', label: 'Sovereign Risk', icon: Shield },
  { key: 'governance', label: 'Governance', icon: Shield },
];

function ComparisonPage() {
  const [countries, setCountries] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [activeSection, setActiveSection] = useState('executive_summary');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      
      const response = await axios.get(`${API}/countries`, {
        
      });
      setCountries(response.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again');
        localStorage.removeItem('token');
        setTimeout(() => { window.location.href = '/login'; }, 1200);
      } else {
        toast.error(error.response?.data?.detail || `Failed to fetch countries${status ? ` (HTTP ${status})` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const addCountry = async (countryId) => {
    if (selectedIds.includes(countryId) || selectedIds.length >= 5) return;
    try {
      
      const response = await axios.get(`${API}/countries/${countryId}`, {
        
      });
      setSelectedIds(prev => [...prev, countryId]);
      setSelectedCountries(prev => [...prev, response.data]);
    } catch (error) {
      toast.error('Failed to load country data');
    }
  };

  const removeCountry = (countryId) => {
    setSelectedIds(prev => prev.filter(id => id !== countryId));
    setSelectedCountries(prev => prev.filter(c => c.id !== countryId));
  };

  const availableCountries = countries.filter(c => !selectedIds.includes(c.id));
  const sectionColors = ['#1E3A5F', '#2563EB', '#059669', '#D97706', '#7C3AED'];

  const renderSectionContent = (country, sectionKey) => {
    const content = country[sectionKey];
    if (!content) {
      return <p className="text-slate-400 italic text-sm">Section not generated</p>;
    }

    if (sectionKey === 'pestle_analysis' && typeof content === 'object') {
      return (
        <div className="space-y-2">
          {Object.entries(content).map(([key, value]) => (
            <div key={key}>
              <p className="font-semibold text-xs uppercase text-slate-500">{key}</p>
              <p className="text-sm text-slate-700 leading-relaxed">{typeof value === 'string' ? value.substring(0, 200) + '...' : ''}</p>
            </div>
          ))}
        </div>
      );
    }

    if (typeof content === 'string') {
      return (
        <div className="prose prose-sm prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-700 prose-p:leading-relaxed prose-table:border prose-td:border prose-td:px-2 prose-td:py-1 prose-th:border prose-th:px-2 prose-th:py-1 prose-th:bg-slate-100 prose-th:text-xs prose-td:text-xs">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </div>
      );
    }

    return <p className="text-sm text-slate-700">{JSON.stringify(content).substring(0, 300)}</p>;
  };

  return (
    <div data-testid="comparison-page" className="bg-[#f8f9ff] min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="cmp"
        eyebrow="Up to 5 countries · side-by-side"
        title="Country"
        accent="Comparison"
        subtitle="Stack five markets across key investment dimensions — economy, risk, sovereign stability, and financial projections — in a single view."
      />
      <div className="p-8">
      {/* Country Selector */}
      <Card className="p-5 mb-6 border border-slate-200 bg-white">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-semibold text-slate-700">Countries:</span>
          {selectedCountries.map((country, idx) => (
            <div key={country.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full border" 
                 style={{ borderColor: sectionColors[idx], backgroundColor: `${sectionColors[idx]}10` }}>
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sectionColors[idx] }} />
              <span className="text-sm font-medium" style={{ color: sectionColors[idx] }}>{country.name}</span>
              <button onClick={() => removeCountry(country.id)} className="ml-1 hover:opacity-70">
                <X className="w-3.5 h-3.5" style={{ color: sectionColors[idx] }} />
              </button>
            </div>
          ))}
          {selectedIds.length < 5 && availableCountries.length > 0 && (
            <Select onValueChange={(val) => addCountry(val)}>
              <SelectTrigger data-testid="add-compare-country" className="w-48 h-9">
                <SelectValue placeholder="+ Add country" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {availableCountries.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      {selectedCountries.length === 0 ? (
        <Card className="p-16 text-center border-slate-200 bg-white">
          <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg mb-2">Select countries to compare</p>
          <p className="text-slate-400 text-sm">Choose 2-5 countries from the dropdown above to see a side-by-side analysis</p>
        </Card>
      ) : (
        <>
          {/* Section Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {COMPARE_SECTIONS.map(section => {
              const Icon = section.icon;
              return (
                <Button
                  key={section.key}
                  variant={activeSection === section.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveSection(section.key)}
                  className={`gap-1.5 whitespace-nowrap ${activeSection === section.key ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
                  data-testid={`compare-tab-${section.key}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {section.label}
                </Button>
              );
            })}
          </div>

          {/* Side-by-Side Grid */}
          <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${selectedCountries.length}, minmax(0, 1fr))` }}>
            {selectedCountries.map((country, idx) => (
              <Card key={country.id} className="border-t-4 bg-white overflow-hidden" style={{ borderTopColor: sectionColors[idx] }}>
                <div className="p-4 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sectionColors[idx] }} />
                    <h3 className="font-bold text-slate-900">{country.name}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{country.sector || 'General'}</p>
                  <p className="text-xs text-slate-400">{country.region || 'Unclassified'}</p>
                </div>
                <div className="p-4 max-h-[65vh] overflow-y-auto">
                  {renderSectionContent(country, activeSection)}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
      </div>
    </div>
  );
}

export default ComparisonPage;
